import { CheckboxInput } from '@frontend/components/basic-forms';
import { EquipmentState } from '@frontend/equipment/types';
import { FormattedMessage, useIntl } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import useLendingReviewResolve from './lending-review-resolve.controller';

const ID = 'lending-review-resolve';

const LendingReviewResolve = () => {
    const intl = useIntl();
    const viewProps = useLendingReviewResolve();

    return (
        <WizardScreenContainer
            id={ID}
            loadingModal={{
                show: viewProps.isProcessing,
                onClose: viewProps.stopProcessing
            }}
            footer={{
                submitDisabled: !viewProps.selectedStatus || (viewProps.removeFromStock && !viewProps.canMaintain),
                onSubmit: viewProps.handleSubmit
            }}>
            <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                <div className='text-foreground-default justify-end'>
                    <FormattedMessage
                        defaultMessage='Selected product'
                        id='kiosk.wizard.lending-review-detail.selected-product'
                        description='Instruction text on the lending review detail screen, showing the selected product'
                    />
                </div>
                <ProductCard
                    product={viewProps.product}
                    equipment={viewProps.equipment}
                />

                <button
                    disabled={!viewProps.canMaintain}
                    className='text-foreground-default disabled:text-brand-disabled bg-background-surface disabled:bg-background-default border-border-default inline-flex items-center justify-start gap-4 self-stretch rounded-xl border p-4'
                    onClick={(e) => {
                        e.stopPropagation();
                        viewProps.toggleRemoveFromStock();
                    }}>
                    <CheckboxInput
                        disabled={!viewProps.canMaintain}
                        value={viewProps.removeFromStock}
                    />
                    <div className='inline-flex flex-1 flex-col items-start justify-center gap-0.5'>
                        <div className='justify-start'>
                            <FormattedMessage
                                defaultMessage='Remove this product from stock'
                                id='kiosk.wizard.lending-review-detail.remove-from-stock'
                                description='Label for the checkbox to remove the selected product from stock'
                            />
                        </div>
                    </div>
                </button>

                <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                    <div className='text-foreground-default justify-end'>
                        <FormattedMessage
                            defaultMessage='My review'
                            id='kiosk.wizard.lending-review-detail.my-review'
                            description='Label for the section where the user can provide their review'
                        />
                    </div>
                    <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                        <RadioButton
                            onClick={() => viewProps.setSelectedStatus(EquipmentState.EQUIPMENT_AVAILABLE)}
                            selected={viewProps.selectedStatus === EquipmentState.EQUIPMENT_AVAILABLE}
                            disabled={!viewProps.availableStatuses.includes(EquipmentState.EQUIPMENT_AVAILABLE)}
                            text={intl.formatMessage({
                                defaultMessage: 'Everything is OK',
                                id: 'kiosk.wizard.lending-review-detail.everything-is-ok'
                            })}
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedStatus(EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED)}
                            selected={viewProps.selectedStatus === EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED}
                            disabled={!viewProps.availableStatuses.includes(EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED)}
                            decline
                            text={intl.formatMessage({
                                defaultMessage: 'Maintenance required',
                                id: 'kiosk.wizard.lending-review-detail.maintenance-required'
                            })}
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedStatus(EquipmentState.EQUIPMENT_BROKEN)}
                            selected={viewProps.selectedStatus === EquipmentState.EQUIPMENT_BROKEN}
                            disabled={!viewProps.availableStatuses.includes(EquipmentState.EQUIPMENT_BROKEN)}
                            decline
                            text={intl.formatMessage({
                                defaultMessage: 'Damaged product',
                                id: 'kiosk.wizard.lending-review-detail.damaged-product'
                            })}
                        />
                        <RadioButton
                            onClick={() => viewProps.setSelectedStatus(EquipmentState.EQUIPMENT_LOST)}
                            selected={viewProps.selectedStatus === EquipmentState.EQUIPMENT_LOST}
                            disabled={!viewProps.availableStatuses.includes(EquipmentState.EQUIPMENT_LOST)}
                            decline
                            text={intl.formatMessage({
                                defaultMessage: 'Wrong product / SLOT empty',
                                id: 'kiosk.wizard.lending-review-detail.wrong-product-slot-empty'
                            })}
                        />
                    </div>
                </div>
            </div>
        </WizardScreenContainer>
    );
};

export default LendingReviewResolve;

const RadioButton = ({
    disabled,
    decline,
    text,
    onClick,
    selected
}: {
    disabled?: boolean;
    decline?: boolean;
    text: string;
    onClick: () => void;
    selected: boolean;
}) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-variant='Detailed'
            className='bg-background-surface disabled:bg-background-default border-border-default inline-flex items-center justify-start gap-4 self-stretch rounded-xl border px-4 py-3'>
            <div
                data-variant='Checked'
                className={`h-4 w-4 ${disabled ? 'bg-background-default' : 'bg-background-surface'} border-border-default flex items-center justify-center rounded-full border`}>
                <div className={`size-2.5 rounded-full ${selected ? 'bg-brand-primary' : ''}`} />
            </div>

            <div className='inline-flex flex-1 flex-col items-start justify-center gap-0.5'>
                <div className={`justify-start ${disabled ? 'text-brand-disabled' : 'text-foreground-default'} text-base font-semibold`}>
                    {decline ? (
                        <FormattedMessage
                            id='decline'
                            defaultMessage='Decline'
                        />
                    ) : (
                        <FormattedMessage
                            id='approve'
                            defaultMessage='Approve'
                        />
                    )}
                </div>
                <div className={`justify-start ${disabled ? 'text-brand-disabled' : 'text-brand-muted'} text-sm font-medium`}>{text}</div>
            </div>
        </button>
    );
};
